// Convert an AIDraftVideo into a ProseMirror JSON doc that matches the
// chosen template's structural shape. Lives in shared/ so both the worker
// (API drafting path) and the client (manual paste-from-Claude.ai path)
// produce identical doc shapes.

import type { AIDraftRow, AIDraftVideo } from './types';
import { DEFAULT_AUDIO_TYPE } from './audioTypes';
import { DEFAULT_VISUAL_TYPE } from './visualTypes';

const para = (text = '', marks?: Array<{ type: string }>) => {
  if (!text) return { type: 'paragraph' };
  return {
    type: 'paragraph',
    content: [{ type: 'text', text, ...(marks ? { marks } : {}) }],
  };
};

const heading = (level: 1 | 2 | 3, text: string) => ({
  type: 'heading',
  attrs: { level },
  content: [{ type: 'text', text }],
});

const labelPara = (label: string, value: string) => ({
  type: 'paragraph',
  content: [
    { type: 'text', marks: [{ type: 'bold' }], text: `${label}: ` },
    { type: 'text', text: value },
  ],
});

const headerRow = () => ({ type: 'scriptHeaderRow' });

// AI-drafted rows default to Voiceover and B-roll like every other new-row
// path (see src/shared/audioTypes.ts and src/shared/visualTypes.ts).
const audioCell = (text: string) => ({
  type: 'scriptCell',
  attrs: { kind: 'audio', audioType: DEFAULT_AUDIO_TYPE },
  content: [para(text)],
});

const visualsCell = (text: string) => ({
  type: 'scriptCell',
  attrs: { kind: 'visuals', visualType: DEFAULT_VISUAL_TYPE },
  content: [para(text)],
});

const row = (audio: string, visuals: string) => ({
  type: 'scriptRow',
  content: [audioCell(audio), visualsCell(visuals)],
});

const bulletItem = (text: string) => ({
  type: 'listItem',
  content: [para(text)],
});

const bulletList = (items: string[]) => ({
  type: 'bulletList',
  content: items.map(bulletItem),
});

function rowsBlock(rows: AIDraftRow[]): unknown[] {
  if (rows.length === 0) {
    return [headerRow(), row('', '')];
  }
  return [headerRow(), ...rows.map((r) => row(r.audio, r.visuals))];
}

export function buildAIDraftDoc(video: AIDraftVideo, clientName: string): unknown {
  const headerBlocks: unknown[] = [
    heading(1, video.title),
    labelPara('Client', clientName),
  ];

  if (video.template_key === 'testimonial' && video.subject) {
    headerBlocks.push(labelPara('Subject', video.subject));
  }
  if (video.template_key === 'social_short') {
    if (video.platform) headerBlocks.push(labelPara('Platform', video.platform));
    if (video.duration) headerBlocks.push(labelPara('Duration', video.duration));
  }
  if (video.template_key === 'fb_ad_campaign') {
    if (video.campaign_objective) {
      headerBlocks.push(labelPara('Campaign objective', video.campaign_objective));
    }
    if (video.audience) headerBlocks.push(labelPara('Audience', video.audience));
  }

  if (video.overview) headerBlocks.push(labelPara('Overview', video.overview));
  if (video.objective) headerBlocks.push(labelPara('Objective', video.objective));

  const bodyBlocks: unknown[] = [];

  if (
    video.template_key === 'testimonial' &&
    video.interview_questions &&
    video.interview_questions.length > 0
  ) {
    bodyBlocks.push(heading(2, 'Interview questions'));
    bodyBlocks.push(bulletList(video.interview_questions));
  }

  if (video.variants && video.variants.length > 0) {
    for (const variant of video.variants) {
      bodyBlocks.push(heading(2, variant.name || 'Variant'));
      bodyBlocks.push(...rowsBlock(variant.rows ?? []));
    }
  } else {
    const sectionLabel =
      video.template_key === 'testimonial' ? 'Script — narration & B-roll' : 'Script';
    bodyBlocks.push(heading(2, sectionLabel));
    bodyBlocks.push(...rowsBlock(video.rows ?? []));
  }

  return {
    type: 'doc',
    content: [...headerBlocks, ...bodyBlocks],
  };
}
